import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { AuthService } from '@lib/services';
import { selectAppSession } from '@lib/store';
import { isValidSession } from '@lib/utils/auth/auth.utils';
import { Store } from '@ngrx/store';
import { firstValueFrom, take } from 'rxjs';

type AuthGuardOptions = {
    requiresAuthentication: boolean;
};

const defaultAuthGuardOptions = (): AuthGuardOptions => ({
    requiresAuthentication: true,
});

export const authGuard = (options: AuthGuardOptions = defaultAuthGuardOptions()): CanMatchFn => {
    const protectedRoutes = ['my-learnings', 'payments'];

    return async (_: Route, segments: UrlSegment[]) => {
        const authService = inject(AuthService);
        const router = inject(Router);
        const store = inject(Store);

        const appSession = await firstValueFrom(store.select(selectAppSession).pipe(take(1)));
        const isAuthenticated = appSession && isValidSession(appSession);
        const currentUrl = segments.map((s) => s.path).join('/');

        if (appSession && !isValidSession(appSession)) {
            authService.logout();
        }

        if (isAuthenticated) {
            authService.initializeSession(appSession);
        }

        if (isAuthenticated && currentUrl.includes('auth')) {
            router.navigateByUrl('/courses');
            return false;
        }

        if (!isAuthenticated && protectedRoutes.some((route) => currentUrl.includes(route))) {
            router.navigateByUrl('/auth/login?returnUrl=' + currentUrl);
            return false;
        }

        return options.requiresAuthentication === isAuthenticated || !options.requiresAuthentication;
    };
};
